import { Link } from "react-router-dom";
import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";

export default function Footer({ variant = "fc" }) {
  const isAnthem = variant === "anthem";
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setStatus(null);
    try {
      await subscribeNewsletter({ email });
      setStatus("You're on the list.");
      setEmail("");
    } catch {
      setStatus("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const cls = isAnthem
    ? "bg-an-bg text-an-ink border-t border-an-border"
    : "bg-fc-ink text-fc-bg border-t border-fc-ink";

  return (
    <footer className={cls} data-testid="site-footer">
      <div className="container-xl py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline mb-4" style={{opacity:0.6}}>Join the Mission</div>
          <h3 className="font-display text-3xl md:text-4xl leading-tight mb-6">
            Stay with the work.
          </h3>
          <p className="text-sm leading-relaxed opacity-70 max-w-md mb-6">
            Updates from the field, new releases, campaign launches, and ways to use the catalog in your advocacy work.
          </p>
          <form onSubmit={submit} className="flex flex-col gap-3 max-w-md" data-testid="footer-newsletter-form">
            <label className="fc-label" style={{color:"inherit", opacity:0.7}}>Email Address</label>
            <div className="flex gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="fc-input flex-1"
                placeholder="you@example.com"
                style={{color:"inherit", borderBottomColor: "currentColor"}}
                data-testid="footer-newsletter-email"
              />
              <button className="btn-primary" disabled={busy} data-testid="footer-newsletter-submit">
                {busy ? "..." : "Subscribe"}
              </button>
            </div>
            {status && <div className="overline mt-2" data-testid="footer-newsletter-status" style={{color:isAnthem?"#B58A3F":"#F9F9F6"}}>{status}</div>}
          </form>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4" style={{opacity:0.6}}>Foster Care Awareness 365</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="link-ul" data-testid="footer-link-home">Home</Link></li>
            <li><Link to="/everyday-awareness" className="link-ul" data-testid="footer-link-awareness">Everyday Awareness</Link></li>
            <li><Link to="/meet-the-kids" className="link-ul" data-testid="footer-link-kids">Meet the Kids</Link></li>
            <li><Link to="/resources" className="link-ul" data-testid="footer-link-resources">Resources</Link></li>
            <li><Link to="/get-involved" className="link-ul" data-testid="footer-link-give">Get Involved</Link></li>
            <li><Link to="/contact" className="link-ul" data-testid="footer-link-contact">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline mb-4" style={{opacity:0.6}}>Foster Care Anthem</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/anthem/mission" className="link-ul" data-testid="footer-link-mission">The Mission</Link></li>
            <li><Link to="/anthem/catalog" className="link-ul" data-testid="footer-link-catalog">The Catalog</Link></li>
            <li><Link to="/anthem/stories" className="link-ul" data-testid="footer-link-stories">Stories</Link></li>
            <li><Link to="/anthem/licensing" className="link-ul" data-testid="footer-link-licensing">Licensing</Link></li>
            <li><Link to="/anthem/were-coming-for-you" className="link-ul" data-testid="footer-link-wcfy">We're Coming For You</Link></li>
            <li><Link to="/behind-the-songs" className="link-ul" data-testid="footer-link-behind">Behind the Songs</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t" style={{borderColor:"currentColor", opacity:0.15}} />
      <div className="container-xl py-10" data-testid="footer-supporters">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-3">
            <div className="overline mb-1" style={{opacity:0.6}}>We Stand With Their Mission</div>
          </div>
          <div className="md:col-span-9 flex flex-wrap items-center gap-6 md:gap-10">
            <a
              href="https://halfamillionkids.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 hover:opacity-100 transition-opacity"
              style={{opacity:0.85}}
              data-testid="supporter-hmk"
            >
              <img
                src="/assets/partner-hmk-logo.jpg"
                alt="Half a Million Kids"
                className="h-12 w-auto object-contain"
                loading="lazy"
              />
              <div className="hidden md:block">
                <div className="font-display text-base leading-tight">Half a Million Kids</div>
                <div className="overline" style={{opacity:0.6}}>halfamillionkids.org ↗</div>
              </div>
            </a>

            <a
              href="https://beingbeautifulfoundation.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 hover:opacity-100 transition-opacity"
              style={{opacity:0.85}}
              data-testid="supporter-bbf"
            >
              <div
                className="h-12 w-12 flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: "#0a0a0a" }}
              >
                <img
                  src="/assets/partner-bbf-logo.png"
                  alt="Being Beautiful Foundation"
                  className="max-h-10 max-w-10 object-contain"
                  loading="lazy"
                />
              </div>
              <div className="hidden md:block">
                <div className="font-display text-base leading-tight">Being Beautiful Foundation</div>
                <div className="overline" style={{opacity:0.6}}>beingbeautifulfoundation.org ↗</div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div className="border-t" style={{borderColor:"currentColor", opacity:0.15}} />
      <div className="container-xl py-10" data-testid="footer-network">
        <div className="grid md:grid-cols-12 gap-6 items-baseline">
          <div className="md:col-span-3">
            <div className="overline mb-1" style={{opacity:0.6}}>Our Network</div>
            <div className="text-xs leading-relaxed" style={{opacity:0.55}}>Sister platforms in the advocacy + media ecosystem.</div>
          </div>
          <ul className="md:col-span-9 flex flex-col md:flex-row md:flex-wrap gap-x-10 gap-y-3 text-sm">
            <li>
              <a
                href="https://phillyfilmsandmusic.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="link-ul inline-flex items-center gap-1.5"
                data-testid="network-philly-films-music"
              >
                Philly Films &amp; Music <ExternalLink size={11} className="opacity-60" />
              </a>
            </li>
            <li>
              <a
                href="https://fostercareanthemproject.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="link-ul inline-flex items-center gap-1.5"
                data-testid="network-foster-care-anthem-project"
              >
                Foster Care Anthem Project <ExternalLink size={11} className="opacity-60" />
              </a>
            </li>
            <li>
              <a
                href="https://getyourrightsready.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="link-ul inline-flex items-center gap-1.5"
                data-testid="network-get-your-rights-ready"
              >
                Get Your Rights Ready <ExternalLink size={11} className="opacity-60" />
              </a>
            </li>
            <li>
              <a
                href="https://groundtogod.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="link-ul inline-flex items-center gap-1.5"
                data-testid="network-ground-to-god"
              >
                Ground to God <ExternalLink size={11} className="opacity-60" />
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t" style={{borderColor:"currentColor", opacity:0.2}} />
      <div className="container-xl py-6 flex flex-col md:flex-row md:justify-between gap-3 text-xs opacity-70">
        <div>© {new Date().getFullYear()} Foster Care Awareness 365 · Foster Care Anthem Project</div>
        <div className="font-mono">fostercareawareness365@gmail.com</div>
      </div>
    </footer>
  );
}
